import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'lucide-react';
import './DateRangePicker.css';

interface DateRangePickerProps {
	initialStartDate?: string; // 'YYYY-MM-DD'
	initialEndDate?: string;
	onRangeChange: (start: string, end: string) => void;
	iconOnly?: boolean;
}

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

const MONTH_OPTIONS = MONTH_NAMES.map((m, i) => ({ value: i, label: m }));
const YEAR_OPTIONS = Array.from({ length: 50 }, (_, i) => {
	const y = new Date().getFullYear() - i;
	return { value: y, label: y };
});

const CustomDropdown = ({
	value,
	options,
	onChange,
}: {
	value: number;
	options: { value: number; label: string | number }[];
	onChange: (val: number) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && listRef.current) {
			const activeEl = listRef.current.querySelector('.active-item');
			if (activeEl) {
				activeEl.scrollIntoView({ block: 'nearest' });
			}
		}
	}, [isOpen]);

	return (
		<div className='relative inline-block' ref={ref}>
			<button
				type='button'
				onClick={() => setIsOpen(!isOpen)}
				className='font-bold text-blue-700 hover:bg-blue-50/50 px-2 py-1 rounded transition-colors outline-none focus:ring-0 focus:outline-none'
			>
				{options.find((o) => o.value === value)?.label}
			</button>
			{isOpen && (
				<div
					ref={listRef}
					className='absolute left-1/2 -translate-x-1/2 top-full mt-1 w-24 max-h-48 overflow-y-auto bg-white border border-gray-100 rounded-lg shadow-xl z-[99999] overflow-hidden text-center font-normal custom-scrollbar'
				>
					{options.map((o) => (
						<div
							key={o.value}
							className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors ${o.value === value ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:text-gray-900'}`}
							onClick={() => {
								onChange(o.value);
								setIsOpen(false);
							}}
						>
							{o.label}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({ initialStartDate, initialEndDate, onRangeChange, iconOnly }) => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const parseDateStr = (str?: string) => {
		if (!str) return null;
		const parts = str.split('-').map(Number);
		if (parts.length !== 3) return null;
		return new Date(parts[0], parts[1] - 1, parts[2]).getTime();
	};
	const formatDateStr = (ms: number) => {
		const d = new Date(ms);
		const yr = d.getFullYear();
		const mo = String(d.getMonth() + 1).padStart(2, '0');
		const da = String(d.getDate()).padStart(2, '0');
		return `${yr}-${mo}-${da}`;
	};
	const formatDisplay = (ms?: number | null) => {
		if (!ms) return '';
		const d = new Date(ms);
		return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].substring(0, 3)} ${d.getFullYear()}`;
	};

	const [start, setStart] = useState<number | null>(parseDateStr(initialStartDate));
	const [end, setEnd] = useState<number | null>(parseDateStr(initialEndDate));
	const [hoverDate, setHoverDate] = useState<number | null>(null);

	const [leftViewYear, setLeftViewYear] = useState<number>(() =>
		start ? new Date(start).getFullYear() : new Date().getFullYear()
	);
	const [leftViewMonth, setLeftViewMonth] = useState<number>(() =>
		start ? new Date(start).getMonth() : new Date().getMonth() - 1
	);

	const [rightViewYear, setRightViewYear] = useState<number>(() =>
		end ? new Date(end).getFullYear() : new Date().getFullYear()
	);
	const [rightViewMonth, setRightViewMonth] = useState<number>(() =>
		end ? new Date(end).getMonth() : new Date().getMonth()
	);

	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const todayMs = today.getTime();

	useEffect(() => {
		setStart(parseDateStr(initialStartDate));
		setEnd(parseDateStr(initialEndDate));
	}, [initialStartDate, initialEndDate]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const navigateMonth = (isLeft: boolean, direction: 'prev' | 'next') => {
		if (isLeft) {
			let y = leftViewYear;
			let m = leftViewMonth;
			if (direction === 'prev') {
				m--;
				if (m < 0) {
					m = 11;
					y--;
				}
			} else {
				m++;
				if (m > 11) {
					m = 0;
					y++;
				}
			}
			if (direction === 'next' && (y > today.getFullYear() || (y === today.getFullYear() && m > today.getMonth())))
				return;
			setLeftViewYear(y);
			setLeftViewMonth(m);
		} else {
			let y = rightViewYear;
			let m = rightViewMonth;
			if (direction === 'prev') {
				m--;
				if (m < 0) {
					m = 11;
					y--;
				}
			} else {
				m++;
				if (m > 11) {
					m = 0;
					y++;
				}
			}
			if (direction === 'next' && (y > today.getFullYear() || (y === today.getFullYear() && m > today.getMonth())))
				return;
			setRightViewYear(y);
			setRightViewMonth(m);
		}
	};

	const handleDayClick = (ms: number, isLeft: boolean) => {
		if (isLeft) {
			if (start === ms) {
				setStart(null);
				onRangeChange('', end ? formatDateStr(end) : '');
			} else {
				setStart(ms);
				if (end !== null && ms > end) {
					setEnd(null);
					onRangeChange(formatDateStr(ms), '');
				} else if (end !== null && ms <= end) {
					onRangeChange(formatDateStr(ms), formatDateStr(end));
				} else {
					onRangeChange(formatDateStr(ms), '');
				}
			}
		} else {
			if (end === ms) {
				setEnd(null);
				onRangeChange(start ? formatDateStr(start) : '', '');
			} else {
				setEnd(ms);
				if (start !== null && ms < start) {
					setStart(null);
					onRangeChange('', formatDateStr(ms));
				} else if (start !== null && ms >= start) {
					onRangeChange(formatDateStr(start), formatDateStr(ms));
				} else {
					onRangeChange('', formatDateStr(ms));
				}
			}
		}
	};

	const renderMonth = (year: number, month: number, isLeft: boolean) => {
		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		const emptyDays = [];
		for (let i = 0; i < firstDay; i++) emptyDays.push(i);

		const days = [];
		for (let day = 1; day <= daysInMonth; day++) {
			days.push(day);
		}

		const disableNextBtn = year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth());

		return (
			<div className='month-container'>
				<div className={`dp-header ${isLeft ? 'left' : 'right'}`}>
					<div className='nav-btn' onClick={() => navigateMonth(isLeft, 'prev')}>
						&#10094;
					</div>
					<div className='month-title flex gap-1 justify-center items-center'>
						<CustomDropdown
							value={month}
							options={MONTH_OPTIONS}
							onChange={(val) => (isLeft ? setLeftViewMonth(val) : setRightViewMonth(val))}
						/>
						<CustomDropdown
							value={year}
							options={YEAR_OPTIONS}
							onChange={(val) => (isLeft ? setLeftViewYear(val) : setRightViewYear(val))}
						/>
					</div>
					<div className={`nav-btn ${disableNextBtn ? 'disabled' : ''}`} onClick={() => navigateMonth(isLeft, 'next')}>
						&#10095;
					</div>
				</div>

				<div className='weekdays'>
					<span>S</span>
					<span>M</span>
					<span>T</span>
					<span>W</span>
					<span>T</span>
					<span>F</span>
					<span>S</span>
				</div>

				<div className='days-grid'>
					{emptyDays.map((i) => (
						<div key={`e-${i}`} className='day empty' />
					))}

					{days.map((day) => {
						const currentMs = new Date(year, month, day).getTime();

						// Interaction logic
						const isFuture = currentMs > todayMs;

						const isGrayedOutRange = isLeft ? end !== null && currentMs > end : start !== null && currentMs < start;

						const disabled = isFuture;

						let classes = ['day'];
						if (disabled) classes.push('disabled');
						else if (isGrayedOutRange) classes.push('grayed-out');

						// Visual range logic
						let currentRangeStart = start;
						let currentRangeEnd = end;

						if (start && !end && hoverDate && hoverDate >= start) {
							currentRangeEnd = hoverDate;
						} else if (!start && end && hoverDate && hoverDate <= end) {
							currentRangeStart = hoverDate;
						}

						if (currentRangeStart && currentMs === currentRangeStart) {
							classes.push('range-start');
						}
						if (currentRangeEnd && currentMs === currentRangeEnd) {
							classes.push('range-end');
						}

						// Real selection markers (Actual State)
						if (start && currentMs === start) classes.push('is-start');
						if (end && currentMs === end) classes.push('is-end');
						if (currentRangeStart && currentRangeEnd && currentMs > currentRangeStart && currentMs < currentRangeEnd) {
							classes.push('in-range');
						}

						return (
							<div
								key={`d-${day}`}
								className={classes.join(' ')}
								onMouseEnter={() => {
									if (!disabled) {
										if (start && !end && isLeft) {
											setHoverDate(null);
										} else if (!start && end && !isLeft) {
											setHoverDate(null);
										} else {
											setHoverDate(currentMs);
										}
									}
								}}
								onMouseLeave={() => {
									setHoverDate(null);
								}}
								onClick={() => {
									if (!disabled) handleDayClick(currentMs, isLeft);
								}}
							>
								<div className='day-text'>{day}</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	return (
		<div className='flex items-center gap-2' ref={containerRef}>
			{!iconOnly && <span className='text-sm font-bold text-gray-500 ml-2'>Joined b/w:</span>}

			<div className='relative flex items-center justify-center'>
				{/* Trigger Button */}
				{iconOnly ? (
					<button
						onClick={() => setIsOpen(!isOpen)}
						className={`group relative flex items-center justify-center w-10 h-[34px] rounded-xl transition-colors focus:ring-2 focus:ring-blue-100 outline-none ${
							start || end ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
						}`}
						title={!start && !end ? 'Filter by Date Joined' : undefined}
					>
						<Calendar className='w-5 h-5' />
						{(start || end) && (
							<div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[99999] whitespace-nowrap bg-gray-800 text-white font-bold text-xs px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none">
								{start && end ? `${formatDisplay(start)} - ${formatDisplay(end)}` : start ? `After ${formatDisplay(start)}` : `Before ${formatDisplay(end)}`}
							</div>
						)}
					</button>
				) : (
					<button
						onClick={() => setIsOpen(!isOpen)}
						className='flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 h-[34px] hover:bg-blue-50/50 transition-colors focus:ring-2 focus:ring-blue-100 outline-none'
					>
						<div className='flex items-center gap-3'>
							<span
								className={`text-sm font-bold w-24 text-center inline-block leading-none ${start ? 'text-blue-700' : 'text-gray-400'}`}
							>
								{start ? formatDisplay(start) : 'Select Start'}
							</span>
							<span className='text-gray-400 font-bold leading-none'>&#10142;</span>
							<span
								className={`text-sm font-bold w-24 text-center inline-block leading-none ${end ? 'text-blue-700' : 'text-gray-400'}`}
							>
								{end ? formatDisplay(end) : 'Select End'}
							</span>
						</div>
					</button>
				)}

				{/* Popover */}
				{isOpen && (
					<div className='absolute top-14 left-1/2 z-[9999] custom-datepicker'>
						{renderMonth(leftViewYear, leftViewMonth, true)}
						<div className='divider' />
						{renderMonth(rightViewYear, rightViewMonth, false)}
					</div>
				)}
			</div>
		</div>
	);
};

export default DateRangePicker;
